import {useStage} from "@client/components/Stage";
import {type CaptureResult, useCapture} from "@client/features/camera";
import * as widgets from "@client/forms/widgets";
import {useSiteContext} from "@client/site/context";
import {ManagerLayout} from "@client/site/ManagerLayout";
import {classNames, reverse, server} from "@reactivated";
import {produce} from "immer";
import React from "react";
import {match} from "ts-pattern";

export const Template = (props: server.manager.reports.ClientManagerSupplements) => {
    const [planner, setPlanner] = React.useState(props.planner);
    const context = useSiteContext();
    const capture = useCapture({
        target: null,
        profile: context.profile!,
        cameraEnabled: false,
    });
    const [supplements, setSupplements] = React.useState(props.supplements);

    const stage = useStage({
        entries: planner.assignments[0].supplements.map((supplement) => ({
            ...supplements.find(
                (s) =>
                    s.backend === supplement.backend &&
                    s.backend_id === supplement.backend_id,
            )!.entry,
            unit: supplement.unit,
            quantity: supplement.target,
            uuid: supplement.uuid as UUID,
        })),
        extra: ({uuid, isRemoved}) => {
            return (
                <div className={classNames(isRemoved == true && "opacity-30")}>
                    <div className="flex -space-x-px">
                        <div className="w-10 sm:w-16">
                            <widgets.DecimalInput
                                integerOnly
                                name={""}
                                className={classNames(
                                    "text-sm block w-full min-w-0 flex-1 rounded-none rounded-l-md border-0 py-1.5 px-2 sm:px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600",
                                    "py-2 focus:z-10 text-xs sm:text-sm",
                                )}
                                onChange={(nextValue) => {
                                    setPlanner((planner) => {
                                        const supplementIndex =
                                            planner.assignments[0].supplements.findIndex(
                                                (s) => s.uuid == uuid,
                                            );

                                        return produce(planner, (draft) => {
                                            for (const assignment of draft.assignments) {
                                                console.log(assignment);
                                                assignment.supplements[
                                                    supplementIndex
                                                ].frequency = nextValue ?? "0";
                                            }
                                        });
                                    });
                                }}
                                value={
                                    isRemoved == true
                                        ? null
                                        : (planner.assignments[0].supplements.find(
                                              (s) => s.uuid == uuid,
                                          )?.frequency ?? null)
                                }
                            />
                        </div>
                        <div className="w-16 sm:w-24">
                            <div
                                className={classNames(
                                    `relative block w-full truncate rounded-r-md border-0 bg-transparent py-2 pr-6 pl-2 text-xs text-gray-900 ring-1 ring-gray-300 ring-inset focus:z-10 focus:ring-2 focus:ring-indigo-600 focus:ring-inset sm:text-sm`,
                                )}
                            >
                                per day
                            </div>
                        </div>
                    </div>
                </div>
            );
        },
        target: null,
    });

    const handleCaptureResult = async (captureResult: CaptureResult) => {
        const entries = await match(captureResult)
            .with({type: "barcode"}, async (r) => {
                const resolved = await capture.resolveBarcode(r.barcode);
                return resolved?.entries ?? null;
            })
            .with({type: "entries"}, (r) => r.entries)
            .otherwise(() => null);

        if (entries != null) {
            const asResults = entries.map((entry) => {
                return {
                    ...entry,
                    backend: entry.backend ?? "",
                    backend_id: entry.backend_id ?? "",
                    source: `${entry.backend ?? ""}-(${entry.backend_id ?? ""})`,
                    entry: entry,
                    name: entry.name,
                    raw: {},
                    shared_uuid_between_supplements_and_entries: crypto.randomUUID(),
                };
            });

            stage.setEntries([
                ...stage.data.entries,
                ...asResults.map((e) => ({
                    ...e.entry,
                    uuid: e.shared_uuid_between_supplements_and_entries,
                    backend: e.backend,
                    backend_id: e.backend_id,
                })),
            ]);

            setSupplements((supplements) => {
                return [...supplements, ...asResults];
            });

            setPlanner((planner) => {
                return produce(planner, (draft) => {
                    asResults.forEach((entry) => {
                        for (const assignment of draft.assignments) {
                            assignment.supplements.push({
                                uuid: entry.shared_uuid_between_supplements_and_entries,
                                backend: entry.backend,
                                backend_id: entry.backend_id,
                                name: entry.name,
                                target: entry.quantity,
                                unit: entry.unit,
                                frequency: "1",
                            });
                        }
                    });
                });
            });
        }
    };

    React.useEffect(() => {}, []);

    return (
        <ManagerLayout
            title="Supplements"
            currentClientManagerId={props.client_manager.id}
        >
            <div>
                <h1 className="flex-1 text-2xl/6 text-gray-900">Supplements</h1>
                <p>
                    This is beta functionality. Use at your own peril. Contact{" "}
                    <strong>support@joyapp.com</strong> to learn how to use it.
                </p>
                {stage.bulkEntries}
                {capture.dialog}
                <button
                    type="button"
                    className="rounded-md bg-blue-500 p-2 text-white"
                    onClick={async () => {
                        const result = await capture.start({
                            initialView: "manual",
                            cameraMode: "barcode_only",
                        });
                        await handleCaptureResult(result);
                    }}
                >
                    Add
                </button>

                <button
                    onClick={async () => {
                        const activeEntries = stage.entriesWithValues.filter(
                            (entry) => stage.data.values[entry.uuid]?.isRemoved != true,
                        );

                        const produced = produce(planner, (draft) => {
                            const toRemove: number[] = [];

                            for (const entry of stage.entriesWithValues) {
                                const supplementIndex =
                                    planner.assignments[0].supplements.findIndex(
                                        (s) => s.uuid == entry.uuid,
                                    );

                                if (stage.data.values[entry.uuid]?.isRemoved == true) {
                                    toRemove.push(supplementIndex);
                                }
                            }

                            for (const entry of stage.entriesWithValues) {
                                const supplementIndex =
                                    planner.assignments[0].supplements.findIndex(
                                        (s) => s.uuid == entry.uuid,
                                    );

                                for (const assignment of draft.assignments) {
                                    assignment.supplements[supplementIndex].target =
                                        entry.quantity;
                                    assignment.supplements[supplementIndex].unit =
                                        entry.unit;
                                }
                            }

                            for (const index of toRemove.reverse()) {
                                for (const assignment of draft.assignments) {
                                    assignment.supplements.splice(index, 1);
                                }
                            }
                        });

                        await server.journal.day.planner({
                            ...produced,
                            assignments: produced.assignments.map((a) => ({
                                ...a,
                                entries: activeEntries,
                            })),
                        });

                        document.location.href = reverse("client_planner", {
                            client_manager_id: props.client_manager.id,
                        });
                    }}
                >
                    Save
                </button>
            </div>
        </ManagerLayout>
    );
};
